import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, QueryList, ViewChildren, ViewEncapsulation } from '@angular/core';
import { ChartComponent, FontModel, IAccTextRenderEventArgs, ILoadedEventArgs, ISeriesRenderEventArgs } from '@syncfusion/ej2-angular-charts';
import { Observable, Subscription } from 'rxjs';
import { DataScopesEnum, ValidationStatusEnum } from '../../shared/enums/index';
import { fadeInOut } from '../../shared/helpers/animations';
import { IIngestedDevicesChart } from '../../shared/models/index';
import { CustomerService } from '../../shared/services/customer.service';

@Component({
  selector: 'app-ingested-devices-chart',
  templateUrl: './ingested-devices-chart.component.html',
  styleUrls: ['./ingested-devices-chart.component.scss'],
  animations: [fadeInOut],
  encapsulation: ViewEncapsulation.None
})
export class IngestedDevicesChartComponent implements OnInit, AfterViewInit, OnDestroy {

  dataScopeEnum: any;
  validationStatusEnum: any;

  // chart init variables
  primaryXAxis: Object;
  primaryYAxis: Object;
  chartData: Object[] = [];
  marker: Object;
  enableAnimation = true;
  legendSettings: Object;
  chartTitle: string;
  tooltip: Object;
  titleStyle: Object;
  chartArea: Object;
  cornerRadius: Object;
  pointColorMapping: string;
  maxCountForYaxis = 0;
  border: Object;
  margin: Object;
  fontModel: FontModel;
  radius: Object;
  subTitle: string;
  subtitleStyle: Object;
  chartComplete = false;

  @Input() customer;
  @Input() user;

  ingestedDevicesData: IIngestedDevicesChart[];

  subsciption: Subscription

  @ViewChildren(ChartComponent) chartsQuery: QueryList<ChartComponent>;
  //@ViewChild('chart-container')
  chart: ChartComponent;


  constructor (private cdRef: ChangeDetectorRef, private customerService: CustomerService) {
    // no action is required in the constructor
  }
  ngOnDestroy(): void {
    if (this.subsciption) {
      this.subsciption.unsubscribe();
    }
  }
  ngAfterViewInit(): void {

    this.chart = this.chartsQuery[0];
    if (this.chart) {
      this.chart.background = '#fff000';
    }

    this.cdRef.detectChanges();
  }

  ngOnInit(): void {

    this.dataScopeEnum = DataScopesEnum;
    this.validationStatusEnum = ValidationStatusEnum;

    this.prepareDataForChart();
  }

  prepareDataForChart() {

    if (this.customer) {
      this.subsciption = this.customerService.getCustomerIngestedDevicesChartData({ 'clientId': this.customer.customerId, 'userGuid': this.user.userguid }).subscribe(data => {
        this.ingestedDevicesData = data;

        // sort array in descending order to get the highest validation count for the yAxis
        if (this.ingestedDevicesData && this.ingestedDevicesData.length) {

          // create a deep independent copy of the original array so it didn't get affected by any action
          let sortedArray: IIngestedDevicesChart[] = [...this.ingestedDevicesData];

          // replace null with 'undefined'
          sortedArray.forEach(element => {
            if (element.validationStatus === null) {
              element.validationStatus = 'undefined';
            }
          });

          // Remove "undefined" and "to be implemented" statuses since they don't have to be displayed.
          sortedArray = sortedArray.filter((status: IIngestedDevicesChart) => status.validationStatus.toLowerCase() !== 'to be implemented' && status.validationStatus.toLowerCase() !== 'undefined');

          if (sortedArray && sortedArray.length) {
            // sort descending by validationStatusCount in order to set the Y Axis to the highest level
            sortedArray.sort((a: IIngestedDevicesChart, b: IIngestedDevicesChart) => b.validationStatusCount - (a.validationStatusCount));
            this.maxCountForYaxis = sortedArray[0].validationStatusCount;
          } else {
            this.maxCountForYaxis = 0;
          }



          // Check data for the current customer application variants
          if (this.customer.applicationVariants && this.customer.applicationVariants.length) {

            this.customer.applicationVariants.forEach(channel => {

              let channelData = {};
              // create a deep independent copy
              const channelArray: IIngestedDevicesChart[] = [...this.ingestedDevicesData].filter((x: IIngestedDevicesChart) => x.channel.toLowerCase() === channel.applicationVariantName.toLowerCase());

              if (channelArray && channelArray.length) {

                // handling null in validation status that breaks the exectution
                channelArray.forEach((element: IIngestedDevicesChart) => {

                  // standardaize the Channel names
                  switch (element.channel.toLowerCase()) {
                    case 'eudamed':
                      element.channel = this.dataScopeEnum.EUDAMED;
                      break;

                    case 'china-udi':
                      element.channel = this.dataScopeEnum.CHINA;
                      break;

                    case 'southkorea-udi':
                      element.channel = this.dataScopeEnum.SKOREA;
                      break;

                    case 'us-udi':
                      element.channel = this.dataScopeEnum.US;
                      break;

                    case 'saudiarabia-udi':
                      element.channel = this.dataScopeEnum.SA;
                      break;
                    case 'australia-udi':
                      element.channel = this.dataScopeEnum.AU;
                      break;
                    case 'brazil-udi':
                      element.channel = this.dataScopeEnum.BRAZIL;
                      break;
                  }
                });

                channelData = {
                  'channel': channelArray[0].channel,
                  'pass': this.getStatusCount(channelArray, this.validationStatusEnum.pass),
                  'warning': this.getStatusCount(channelArray, this.validationStatusEnum.warning),
                  'fail': this.getStatusCount(channelArray, this.validationStatusEnum.fail),
                };

              } else {
                channelData = {
                  'channel': channel.applicationVariantName,
                  'pass': 0,
                  'warning': 0,
                  'fail': 0
                };
              }

              this.chartData.push(channelData);
            });
          }
        }

        this.initChart();
        this.chartComplete = true;
      });
    }
  }

  getStatusCount(channelArray: IIngestedDevicesChart[], status: string): number {

    let count = 0;

    if (status && channelArray && channelArray.length) {

        if (channelArray.find(x => x.validationStatus.toLowerCase() === status.toLowerCase())) {
          count += channelArray.find(x => x.validationStatus.toLowerCase() === status.toLowerCase()).validationStatusCount;
        }
      return count;
    }
  }

  private initChart() {

    this.radius = { bottomLeft: 0, bottomRight: 0, topLeft: 7, topRight: 7 };

     this.primaryXAxis = {
      valueType: 'Category',
      title: '',
      majorGridLines: { width: 0 },
      majorTickLines: { width: 1 },
      border: { width: 0, color: '#bbb' }
    };


    let multiplier = this.maxCountForYaxis * 1.1;
    let percent = 1.1;

    //add 10% so the data label stays above.
    // If this.maxCountForYaxis = 0 set a default maximum to 10 and interval to 2
    const maximumY = this.maxCountForYaxis ? (Math.ceil(this.maxCountForYaxis / multiplier) * multiplier) * percent : 10;
    const intervalY = maximumY ? Math.ceil(maximumY / 7) : 2;

    // max and interval rounded to a closest thousand
    this.primaryYAxis = {
      title: 'Validated Devices',
      minimum: 0,
      maximum: maximumY,
      interval: intervalY,
      majorGridLines: { width: 1 },
      majorTickLines: { width: 2 }
    };

    this.fontModel = {
      fontWeight: 'regular'
    }

    this.marker = {
      dataLabel: {
        visible: true,
        font: this.fontModel,
        // fill: '#f2f2f2',
        // border: { width: 1, color: '#ddd' },
        margin: {
          left: 5,
          right: 5,
          top: 1,
          bottom: 1
        }
      }
    };

    this.legendSettings = {
      visible: true,
      position: 'Top',
      shapeHeight: 15,
      shapeWidth: 15,
    //  toggleVisibility: false
    };

    this.tooltip = { enable: true };

    this.titleStyle = {
      fontFamily: 'open_sansregular, sans-serif',
      color: '#0071bb',
      size: '18px',
      textWrap: 'Wrap'
    };

    this.subtitleStyle = {
      fontFamily: 'open_sansregular, sans-serif',
      color: '#0071bb',
      size: '14px',
      textWrap: 'AnyWhere'
    };

    this.chartTitle = `Device Validations by Health Authority`;
    this.subTitle = ` ${ this.customer.customerName } `;

    this.chartArea = {
      border: {
        width: 0,
        color: '#aaa'
      }
    };
    this.cornerRadius = {
      topLeft: 5, topRight: 5
    };

    this.pointColorMapping = 'color';
    this.border = { width: 1, color: '#0071bb' }; // [border]='border'
    this.margin = { left: 20, right: 20, top: 20, bottom: 20 }; // [margin]='margin'
  }

  public load(args: ILoadedEventArgs): void {

   // If series equal 0 - hide the series
    //  for (let i: number = 0; i < args.chart.series.length; i++) {

    //   for (let k: number = 0; k < (args.chart.series[i].dataSource as any).length; k++) {

    //     if (args.chart.series[i].dataSource[k][args.chart.series[i].yName] === 0) {

    //       args.chart.series[i].visible = false;
    //       args.chart.series[i].name = "";
    //     }
    //   }
    // }
  }

  seriesRender(args: ISeriesRenderEventArgs) {

    let index = args.series['index'];
  }

  onTextRender(args: IAccTextRenderEventArgs): void {
    args.text = `${ args.point.y }`;

    let index = args.series['index'];

    // to prevent displaying 0
    if (args.text === '0') {
      args.cancel = args.point.y === 0;
    }
  }
}
